import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodePublicRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { ReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { RepliesRelayResponseDto } from '@diritto/common/relay/dtos/reply-relay.dtos';
import { accurateItems, flatItems } from '@diritto/common/utils/array';
import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { RelayReplyApi } from 'api/ApiHelper';
import { useAuthState } from 'contexts/AuthProvider';
import { useSearchParams } from 'next/navigation';
import { useGetLikedRepliesQueries } from 'queries/reply/useGetLikedRepliesQuery';
import { useEffect, useMemo, useState } from 'react';

export const useGetReplyInfo = <R extends RepliesRelayResponseDto>(
  replyQuery: UseInfiniteQueryResult<InfiniteData<R>, unknown>,
) => {
  const { isLoggedIn } = useAuthState();
  const { data, hasNextPage, fetchNextPage } = replyQuery;
  const { replies, profiles, contents, episodes } = useMemo(
    () => ({
      replies: flatItems<ReplyRelayDto>('replies', data?.pages) ?? [],
      contents: accurateItems<ContentPublicRelayDto>('contents', data?.pages) ?? {},
      episodes: accurateItems<EpisodePublicRelayDto>('episodes', data?.pages) ?? {},
      profiles: accurateItems<ProfileLitePublicRelayDto>('profileLites', data?.pages) ?? {},
    }),
    [data?.pages],
  );

  // Reply Like
  const replyIds = useMemo(() => replies.map(it => it.id) ?? [], [replies]);
  const queries = useGetLikedRepliesQueries(replyIds, replyIds.length > 0 && isLoggedIn);
  const likeReplyIds: number[] = useMemo(
    () => queries.reduce((acc, query) => [...acc, ...(query.data?.likeReplyIds ?? [])], [] as number[]),
    [queries],
  );

  // Focused Reply
  const searchParams = useSearchParams();
  const [focusedReply, setFocusedReply] = useState<ReplyRelayDto>();
  const focusedReplyId = Number(searchParams?.get('focusedReplyId'));
  useEffect(() => {
    if (focusedReplyId) {
      RelayReplyApi.getReply(focusedReplyId)
        .then(focusedReplyData => !focusedReplyData.reply.isDeleted && setFocusedReply(focusedReplyData.reply))
        .catch(() => {});
    }
  }, [focusedReplyId]);

  useEffect(() => {
    if (focusedReplyId && !replies.find(reply => reply.id === focusedReplyId) && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, focusedReplyId, hasNextPage, replies]);

  return {
    replies,
    profiles,
    likeReplyIds,
    contents,
    episodes,
    focusedReplyId,
    focusedReply,
  };
};
